/**
 * Vorlage „Schaumig schlagen“.
 *
 * Butter und Zucker werden mit dem Handrührgerät geschlagen. Die Masse ist
 * anfangs dunkler und körnig, wird dann hell, luftig und nimmt an Volumen zu.
 */
import { ease, fract, lerp, segment, wave } from '../geometry.ts';
import { path } from '../paths.ts';
import { circle, compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { motionLines } from '../parts/effects.ts';
import { arcArrow, counter, BADGE_RIGHT, durationLabel, timerBadge } from '../parts/stage.ts';
import { handMixer, place } from '../parts/utensils.ts';
import { bowl, interiorClip } from '../parts/vessels.ts';
import { minutesFromText } from '../inference.ts';
import { defineTemplate, numberValue } from '../template.ts';

const BOWL = { cx: 104, rim: 80, width: 140, height: 54 } as const;

/** Zeitplan. */
const TIME = { cream: [0.04, 0.8], reset: [0.9, 1] } as const;

/**
 * Masse als Körper: hinten ein welliges Profil, das mit dem Aufschlagen
 * höher und weicher wird, vorn die halbe Oberflächenellipse.
 */
function massBody(cx: number, y: number, rx: number, ry: number, fluff: number): string {
  const steps = 24;
  const builder = path().move(cx - rx, y);
  for (let index = 1; index < steps; index += 1) {
    const f = index / steps;
    const back = -Math.sin(f * Math.PI) * ry;
    const dome = Math.sin(f * Math.PI) ** 0.7 * 8 * fluff;
    const bumps = (wave(f, 4, 0.2) * 1.4 + wave(f, 7) * 0.8) * (1 - fluff * 0.4) + wave(f, 2.5) * 2.5 * fluff;
    builder.line(cx - rx + f * rx * 2, y + back - dome + bumps);
  }
  builder.line(cx + rx, y);
  builder.arc(rx, ry, false, true, cx - rx, y);
  return builder.close().toString();
}

/** Vorlage „Schaumig schlagen“. */
export const mixCreamTemplate = defineTemplate({
  id: 'mix.cream',
  group: 'mix',
  title: 'Schaumig schlagen',
  description:
    'Weiche Butter und Zucker werden mit dem Handrührgerät geschlagen, bis die Masse hell, cremig und deutlich mehr geworden ist.',
  durationSeconds: 6,
  keywords: [
    'schaumig',
    'cremig rühren',
    'cremig schlagen',
    'handrührgerät',
    'handmixer',
    'rührgerät',
    'küchenmaschine',
    'hell und cremig',
    'butter und zucker',
  ],
  stillTime: 0.7,
  parameters: [
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 1,
      max: 20,
      step: 1,
      unit: 'Min',
      defaultValue: 5,
    },
  ],
  inferValues(text) {
    return { minutes: minutesFromText(text) };
  },
  scene(values, t) {
    const minutes = numberValue(values, 'minutes', 5);
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const cream = ease.inOutSine(segment(t, ...TIME.cream));
    const mass = (amount: number): SceneNode => {
      const level = lerp(0.5, 0.32, amount);
      const y = BOWL.rim + vessel.depth * level;
      const rx = vessel.widthAt(level);
      const ry = vessel.ry * (rx / vessel.rx);
      const body = massBody(BOWL.cx, y, rx, ry, amount);
      // Zuckerkörner sind nur am Anfang zu sehen.
      const grains =
        amount < 0.5
          ? [-30, -18, -4, 10, 24, 34, -24, 4, 18].map((dx, index) =>
              circle(BOWL.cx + dx, y + ((index % 3) - 1) * 3.5, 1.1, styles.flat('paper')),
            )
          : [];
      return group(
        [
          shape(body, amount < 0.5 ? { fill: 'shade', stroke: 'ink', strokeWidth: 2 } : styles.snow()),
          group(grains, { clip: body }),
        ],
        { clip: interiorClip(vessel, 24) },
      );
    };
    const level = lerp(0.5, 0.32, cream * (1 - reset));
    const surfaceY = BOWL.rim + vessel.depth * level;
    const orbit = t * Math.PI * 2;
    const mixer = place(
      BOWL.cx + Math.cos(orbit) * 18,
      surfaceY + 6 + Math.sin(orbit) * 3,
      Math.cos(orbit) * 6,
      handMixer(fract(t * 24)),
      0.85,
    );
    return compact([
      ...counter(),
      ...vessel.back,
      reset > 0 ? group([mass(0)]) : null,
      faded(1 - reset, [mass(cream)]),
      mixer,
      ...vessel.front,
      motionLines(BOWL.cx + BOWL.width / 2 + 10, surfaceY - 4, 200, 12, 3, 6, 0.85),
      timerBadge(BADGE_RIGHT, 24, durationLabel(minutes), t),
    ]);
  },
  hints() {
    return [arcArrow(BOWL.cx, BOWL.rim - 2, 40, 10, 200, 340)];
  },
});
